import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as TimerIcon } from '../../../assets/icons/clock-icon.svg';
import EmailVerifyBanner from '../../../assets/images/register/otp-verify-banner.png';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomOtpInput from '../../../shared-components/custom-otp-input/CustomOtpInput';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import auth from '../../../utils/api/auth';

import OtpVerifyBanner from './OtpVerifyBanner';

/**
 * Phone number verification page
 */
function PhoneNumberVerification() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accountVerificationStatus = getLocalStorageItem('accountVerificationStatus');
  const [OTP, setOTP] = useState<string[]>(Array(6).fill(''));
  const [resendTimer, setResendTimer] = useState<number>(30);
  const intervalReference = useRef<NodeJS.Timeout | null>(null);

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  /**
   * Verify entered OTP and handle navigation
   */
  const handleSubmitOtp = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (!OTP.some((digit) => digit === '')) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });
      const phoneOTP = OTP.join('');
      auth
        .verifyMobile({ otp: phoneOTP })
        .then(() => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          const userVerification = getLocalStorageItem('userVerification');

          let updatedUserVerification;
          if (userVerification?.redirectToEmailVerification) {
            updatedUserVerification = {
              ...userVerification,
              redirectToPhoneVerification: false,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/email-verification');
          } else if (!userVerification?.isInitialPasswordReset) {
            updatedUserVerification = {
              ...userVerification,
              redirectToPhoneVerification: false,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/reset-initial-password');
          } else {
            removeLocalStorageItem('userVerification'); // Remove user verification from local storage
            if (accountVerificationStatus === 'approved') {
              navigate('../ev-admin/dashboard');
            } else {
              navigate('../ev-admin/profile');
            }
          }
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          // Display a user-friendly message
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  /**
   * Function to handle Resend OTP timer
   */
  const startResendTimer = () => {
    // Clear any previous interval before starting a new one
    if (intervalReference.current) clearInterval(intervalReference.current);

    setResendTimer(30); // Reset to 60 seconds
    intervalReference.current = setInterval(() => {
      setResendTimer((previous) => {
        if (previous <= 1) {
          if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval at the end
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };

  /**
   * Function to resend OTP
   */
  const handleResendOTP = () => {
    if (resendTimer === 0) {
      startResendTimer();
      auth
        .resendMobileOTP()
        .then(() => {})
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    startResendTimer(); // Start the timer when the component mounts
    return () => {
      if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval on unmount
    };
  }, []);

  return (
    <div className="verification-wrap">
      <div className="otp-banner">
        <OtpVerifyBanner
          imageSrc={EmailVerifyBanner}
          bannerTittle="Enter the OTP"
          bannerDescription="Please enter the OTP. Received that which you registered Phone Number"
        />
      </div>
      <div className="otp-wrap">
        <div className="otp-container">
          <h1>Enter the OTP (Phone Number)</h1>
          <CustomOtpInput
            length={6}
            OTP={OTP}
            setOTP={setOTP}
            type="number"
            inputLabel="Enter OTP"
            error={formStatus.showFormErrors && OTP.some((digit) => digit === '')}
          />
          <div className="resend-otp-wrap">
            <div className="resend-timer-wrap">
              <span className="resend-icon">
                <TimerIcon />
              </span>
              <span>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</span>
            </div>
            <p
              role="presentation"
              onClick={handleResendOTP}
              className={`resendOtp ${resendTimer > 0 ? 'disabled' : ''}`}>
              Resend
            </p>
          </div>
          <div className="submit-button-container">
            <MyButton
              label="Proceed"
              buttonType="submit"
              onClickFunc={handleSubmitOtp}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneNumberVerification;
